class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus() {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      this.onChange();
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][fields]', 'title,tag,product_type,vendor,variants.title,variants.sku'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  buildTagURL(query) {
    if (!query || query.length < 3) return null;
    const handle = query.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
    if (!handle) return null;
    return `/collections/all/${handle}?section_id=tag-search-results`;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const query = this.getQuery();
    const predictiveUrl = this.setupURL().toString();
    const tagUrl = this.buildTagURL(query);
    const cacheKey = predictiveUrl + (tagUrl || '');

    if (this.cachedMap.has(cacheKey)) {
      this.renderSearchResults(this.cachedMap.get(cacheKey));
      this.setAttribute('results', '');
      return;
    }

    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    const predictiveFetch = fetch(predictiveUrl)
      .then((r) => { if (!r.ok) throw new Error('Network response was not ok'); return r.text(); });
    const tagFetch = tagUrl
      ? fetch(tagUrl).then((r) => r.ok ? r.text() : '').catch(() => '')
      : Promise.resolve('');

    Promise.all([predictiveFetch, tagFetch])
      .then(([predictiveHtml, tagHtml]) => {
        const mergedHtml = this.mergeAndRender(predictiveHtml, tagHtml);
        this.renderSearchResults(mergedHtml);
        this.cachedMap.set(cacheKey, mergedHtml);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }

  mergeAndRender(predictiveHtml, tagHtml) {
    const parser = new DOMParser();
    const predictiveDoc = parser.parseFromString(predictiveHtml, 'text/html');

    if (!tagHtml) return predictiveHtml;

    const tagDoc = parser.parseFromString(tagHtml, 'text/html');
    const tagContainer = tagDoc.getElementById('TagSearchResults');
    if (!tagContainer) return predictiveHtml;

    const tagItems = tagContainer.querySelectorAll('li');
    if (tagItems.length === 0) return predictiveHtml;

    // Collect existing product URLs from predictive results
    const productList = predictiveDoc.getElementById('predictive-search-results-products-list');
    const existingUrls = new Set();

    if (productList) {
      productList.querySelectorAll('a').forEach((a) => {
        const href = a.getAttribute('href');
        if (href) existingUrls.add(href.split('?')[0]);
      });
    }

    // Filter out duplicates from tag results
    const newItems = [];
    tagItems.forEach((li) => {
      const url = li.dataset.productUrl;
      if (url && !existingUrls.has(url.split('?')[0])) {
        existingUrls.add(url.split('?')[0]);
        newItems.push(li);
      }
    });

    if (newItems.length === 0) return predictiveHtml;

    if (productList) {
      // Append tag products to existing product list
      newItems.forEach((li) => productList.appendChild(predictiveDoc.importNode(li, true)));
    } else {
      // No predictive products — create the products section
      const resultsContainer = predictiveDoc.querySelector('.grid.gap-6.w-full');
      if (resultsContainer) {
        const section = predictiveDoc.createElement('div');
        section.className = 'grid gap-3 predictive-search-result search__box-item predictive-search-result--products';
        section.innerHTML = `<h4 class="h5 predictive-search-result__heading">${this.productsHeading}</h4><div class="grid"><ul id="predictive-search-results-products-list" class="grid gap-4" role="group" aria-labelledby="predictive-search-products"></ul></div>`;
        const ul = section.querySelector('ul');
        newItems.forEach((li) => ul.appendChild(predictiveDoc.importNode(li, true)));
        resultsContainer.appendChild(section);
      } else {
        // Predictive search returned zero results entirely — build the full results wrapper
        const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
        const wrapper = predictiveDoc.getElementById(id);
        if (wrapper) {
          wrapper.innerHTML = `<div class="grid gap-6 w-full"><div class="grid gap-3 predictive-search-result search__box-item predictive-search-result--products"><h4 class="h5 predictive-search-result__heading">${this.productsHeading}</h4><div class="grid"><ul id="predictive-search-results-products-list" class="grid gap-4" role="group" aria-labelledby="predictive-search-products"></ul></div></div></div><div class="search__results-all block"><button type="submit" class="reversed-link btn--inherit flex items-center justify-center gap-2 font-body-bolder">${this.searchForText}</button></div>`;
          const ul = wrapper.querySelector('ul');
          newItems.forEach((li) => ul.appendChild(predictiveDoc.importNode(li, true)));
        }
      }
    }

    return predictiveDoc.documentElement.outerHTML;
  }

  get productsHeading() {
    return document.querySelector('.predictive-search-result--products .predictive-search-result__heading')?.textContent
      || document.querySelector('[data-products-heading]')?.textContent
      || 'Products';
  }

  get searchForText() {
    return '';
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
